import { TFormValues, useNoteQueryKey } from '.';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useFormContext } from 'react-hook-form';

import { useEmployersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import type { Note as TNote } from '@restworld/utility-types';
import { scrollToView } from '@restworld/utils-common';

import { selectedJobApplicationAtom } from '../../../../employer-job-listing.atoms';

export const useNoteMutation = () => {
  const { reset } = useFormContext<TFormValues>();
  const queryKey = useNoteQueryKey();
  const employerServices = useEmployersService();
  const queryClient = useQueryClient();
  const userData = useAuth().user;
  const jobApp = useAtomValue(selectedJobApplicationAtom);

  const { mutate: updateNoteMutation, isPending: isUpdateNotePending } = useMutation({
    mutationFn: (params: { noteId: number; note: string }) => employerServices.updateNote(params),
    onMutate: async ({ noteId, note }) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousNotes = queryClient.getQueryData<TNote[]>(queryKey);
      if (previousNotes) {
        queryClient.setQueryData<TNote[]>(
          queryKey,
          previousNotes.map((n) => (n.id === noteId ? { ...n, note } : n))
        );
      }
      return { previousNotes };
    },
    onSuccess() {
      reset({ note: '' });
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
    onError: (_err, _, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(queryKey, context.previousNotes);
      }
    },
  });

  const { mutate: addNoteMutation, isPending: isAddNotePending } = useMutation({
    mutationFn: (params: { note: string }) =>
      employerServices.insertNote({
        note: params.note,
        entityCategory: 'employer_worker_job_positions',
        jobApplicationId: jobApp?.worker_id + (jobApp?.job_position_id || jobApp?.restaurant_id),
      }),
    onMutate: async ({ note }) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousNotes = queryClient.getQueryData<TNote[]>(queryKey);
      if (previousNotes) {
        queryClient.setQueryData<TNote[]>(queryKey, [
          ...previousNotes,
          {
            id: Math.random(),
            note,
            inserted_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_modified_by_user: { ...userData, has_signed_up: null, updated_at: null },
          },
        ]);
      }
      return { previousNotes };
    },
    onSuccess(data) {
      reset({ note: '' });
      queryClient.invalidateQueries({ queryKey });
      setTimeout(() => {
        scrollToView(`${data.data.id}`);
      }, 500);
    },
    onError: (_err, _, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(queryKey, context.previousNotes);
      }
    },
  });

  const { mutate: deleteNoteMutation, isPending: isDeleteNotePending } = useMutation({
    mutationFn: (noteId: number) => employerServices.removeNote(noteId),
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey });
      const previousNotes = queryClient.getQueryData<TNote[]>(queryKey);
      if (previousNotes) {
        queryClient.setQueryData<TNote[]>(
          queryKey,
          previousNotes.filter((n) => n.id !== noteId)
        );
      }
      return { previousNotes };
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (_err, _, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(queryKey, context.previousNotes);
      }
    },
  });
  return {
    updateNoteMutation,
    deleteNoteMutation,
    addNoteMutation,
    isPending: isAddNotePending || isDeleteNotePending || isUpdateNotePending,
  };
};
